import { X } from "lucide-react";
import React from "react";
import clsx from "clsx";
import AssistantMessage from "./assistantMessage";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

/**
 * Props for the ChatMessageModal component
 */
interface ChatMessageModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback fired when modal is closed */
  onClose: () => void;
  /** Markdown message content to display */
  message: string;
  /** Modal title. Defaults to "Full Response". */
  title?: string;
}

/**
 * ChatMessageModal component displays a full message in a dialog modal with
 * markdown rendering support. Used to show complete message content in a larger view.
 * 
 * @component
 * @param {ChatMessageModalProps} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {() => void} props.onClose - Close callback
 * @param {string} props.message - Markdown message content
 * @param {string} [props.title="Full Response"] - Modal title
 * @returns {JSX.Element | null} Dialog modal with message content or null if not open
 * 
 * @example
 * ```tsx
 * <ChatMessageModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   message={messageContent}
 *   title="View Full Message"
 * />
 * ```
 */
const ChatMessageModal: React.FC<ChatMessageModalProps> = ({
  open,
  onClose,
  message,
  title = "Full Response",
}) => {
  if (!open) return null;

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-[700px]">
        <DialogTitle className="flex justify-between m-0 items-center">
          {title}
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogTitle>
        <div className="overflow-y-auto max-h-[70svh] pr-4">
          <AssistantMessage message={message} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatMessageModal;
